import { useState } from 'react';
import { Lead, TruckType } from '@otw/models/leads/lead';
import { Vehicle } from '@otw/models/inventory/inventory';

// Define similarity levels
type SimilarityLevel = 'exact' | 'similar' | 'none';

interface VehicleWithSimilarity extends Vehicle {
  similarityLevel: SimilarityLevel;
  matchScore: number;
  matchingType?: TruckType;
  typeKey?: string;
}

/**
 * Hook for calculating similarity between vehicles and lead truck types
 */
export const useSimilarityCalculation = () => {
  const [filteredVehicles, setFilteredVehicles] = useState<VehicleWithSimilarity[]>([]);
  const [selectedTruckTypes, setSelectedTruckTypes] = useState<string[]>([]);

  /**
   * Helper function to get display name for a truck type
   */
  const getDisplayName = (truckType: any): string => {
    const parts = [];
    
    // Handle type display
    if (truckType.type) {
      if (Array.isArray(truckType.type)) {
        parts.push(truckType.type.join('/'));
      } else {
        parts.push(truckType.type);
      }
    }
    
    // Handle size display
    if (truckType.size) {
      if (Array.isArray(truckType.size)) {
        parts.push(truckType.size.join('/'));
      } else {
        parts.push(truckType.size);
      }
    }
    
    // Handle make and model display
    if (truckType.make) {
      if (Array.isArray(truckType.make)) {
        parts.push(truckType.make.join('/'));
      } else {
        parts.push(truckType.make);
      }
    }
    
    if (truckType.model) {
      if (Array.isArray(truckType.model)) {
        parts.push(truckType.model.join('/'));
      } else {
        parts.push(truckType.model);
      }
    }
    
    return parts.join(' - ') || 'Any Truck';
  };

  /**
   * Calculates similarity between a vehicle and the truck types requested in a lead
   * 
   * @param vehicle - The vehicle to check
   * @param lead - The lead with requested truck types
   * @returns VehicleWithSimilarity - The vehicle with similarity information
   */
  const calculateVehicleSimilarity = (
    vehicle: Vehicle,
    lead: Lead
  ): VehicleWithSimilarity => {
    // Using the original algorithm from LeadDetailDialog
    if (!lead.truckRequest?.types || lead.truckRequest.types.length === 0) {
      return {
        ...vehicle,
        similarityLevel: 'none',
        matchScore: 0
      };
    }

    // Initialize best match variables
    let bestMatchScore = 0;
    let bestMatchType: TruckType | undefined;
    let bestMatchLevel: SimilarityLevel = 'none';
    let bestMatchTypeKey = '';

    // Check each truck type in the lead
    lead.truckRequest.types.forEach((truckType, index) => {
      // Skip if no type or size specified
      if ((!truckType.type || truckType.type.length === 0) && 
          (!truckType.size || truckType.size.length === 0)) return;

      // Check if vehicle type matches any of the requested types
      if (truckType.type && truckType.type.length > 0 && vehicle.type) {
        const typesList = Array.isArray(truckType.type) ? truckType.type : [truckType.type];
        if (!typesList.includes('Any') && !typesList.includes(vehicle.type)) {
          return; // Skip if types don't match
        }
      }

      // Calculate similarity using the original algorithm
      const { score, level } = calculateSimilarityScore(vehicle, truckType);
      
      // If this match is better than previous best, update best match
      if (score > bestMatchScore || (score === bestMatchScore && level === 'exact' && bestMatchLevel !== 'exact')) {
        bestMatchScore = score;
        bestMatchType = truckType;
        bestMatchLevel = level;
        bestMatchTypeKey = `type-${index}`;
      }
    });

    return {
      ...vehicle,
      similarityLevel: bestMatchLevel,
      matchScore: bestMatchScore,
      matchingType: bestMatchType,
      typeKey: bestMatchTypeKey
    };
  };

  /**
   * Calculate similarity score using the original algorithm
   */
  const calculateSimilarityScore = (vehicle: Vehicle, truckType: TruckType): { score: number, level: SimilarityLevel } => {
    let score = 0;
    const maxScore = 10; // Maximum possible score
    let exceedsMileageLimit = false;
    let sizeMismatch = false;
    let fuelTypeMismatch = false;
    
    // Type match - handle type as array
    const typesList = Array.isArray(truckType.type) ? truckType.type : [truckType.type];
    if (typesList.includes('Any') || (vehicle.type && typesList.includes(vehicle.type))) {
      score += 3;
    } else {
      // If we got here, there should be a match since we pre-filtered
      score += 3;
    }
    
    // Size match - STRICT ENFORCEMENT - handle size as array
    if (truckType.size && vehicle.size) {
      const sizesList = Array.isArray(truckType.size) ? truckType.size : [truckType.size];
      if (sizesList.includes('Any') || sizesList.includes(vehicle.size)) {
        score += 2;
      } else {
        // If sizes don't match exactly, mark as mismatch
        sizeMismatch = true;
      }
    }
    
    // Fuel type match - STRICT ENFORCEMENT
    if (truckType.fuelType && vehicle.fuel) {
      // Handle fuelType as array
      const fuelTypesList = Array.isArray(truckType.fuelType) ? truckType.fuelType : [truckType.fuelType];
      if (fuelTypesList.includes('Any') || fuelTypesList.includes(vehicle.fuel)) {
        score += 0.5;
      } else {
        // If fuel types don't match, mark as mismatch
        fuelTypeMismatch = true;
      }
    }
    
    // Condition match
    if (truckType.condition && vehicle.condition) {
      if (truckType.condition === vehicle.condition) {
        score += 1;
      }
    }
    
    // Make match
    if (truckType.make && vehicle.make) {
      // Handle make as array
      const makesList = Array.isArray(truckType.make) ? truckType.make : [truckType.make];
      if (makesList.includes('Any') || makesList.includes(vehicle.make)) {
        score += 1;
      }
    }
    
    // Model match
    if (truckType.model && vehicle.model) {
      // Handle model as array
      const modelsList = Array.isArray(truckType.model) ? truckType.model : [truckType.model];
      if (modelsList.includes('Any') || modelsList.includes(vehicle.model)) {
        score += 1;
      }
    }
    
    // Year range match
    if (vehicle.year) {
      let yearScore = 0;
      
      if (truckType.minYear && vehicle.year >= truckType.minYear) {
        yearScore += 1;
      }
      
      // If no year range specified, give full points
      if (!truckType.minYear) {
        yearScore = 1;
      }
      
      score += yearScore;
    }
    
    // Mileage match - STRICT ENFORCEMENT
    if (truckType.maxMileage && vehicle.miles) {
      // maxMileage is now always a number, no need for conversion
      const requestedMaxMileage = truckType.maxMileage;
      
      // Check if vehicle exceeds max mileage by more than 10,000 miles
      if (vehicle.miles > requestedMaxMileage + 15000) {
        exceedsMileageLimit = true;
        score -= 1; // Penalty for exceeding max mileage
      } else if (vehicle.miles <= requestedMaxMileage) {
        // Vehicle is within requested mileage
        score += 0.5;
      }
    }
    
    let exceedBudget = false;
    // Budget match
    if (truckType.budget && vehicle.optic_list_price) {
      const budget = typeof truckType.budget === 'string' 
        ? parseFloat(String(truckType.budget).replace(/[^\d.]/g, '')) 
        : truckType.budget;
      
      if (budget) {
        // Exact match (within 10%)
        if (vehicle.optic_list_price <= budget) {
          score += 0.5;
        }
        // Similar match (within 10%)
        else if (vehicle.optic_list_price <= budget * 1.2) {
          score += 0;
        }
        else if (vehicle.optic_list_price > budget * 1.2) {
          exceedBudget = true;
        }
      }
    }
    
    // Calculate similarity level based on score percentage
    const percentage = (score / maxScore) * 100;
    
    // Determine similarity level - only 'exact' or 'similar', no 'partial'
    let level: SimilarityLevel;
    
    // If size or fuel type doesn't match exactly, immediately set to 'none'
    if (sizeMismatch || fuelTypeMismatch) {
      level = 'none';
    }
    else if (percentage >= 90 && !exceedsMileageLimit && !exceedBudget) {
      level = 'exact';
    } 
    else if (percentage >= 80 && !exceedsMileageLimit && !exceedBudget) {
      level = 'similar';
    }
    else {
      level = 'none';
    }

    return { score, level };
  };

  /**
   * Calculates similarity for a list of vehicles against a lead's truck types
   * Only includes vehicles with status 'Available', 'Pending Available', or 'Active Deal'
   * 
   * @param vehicles - Array of vehicles to check
   * @param lead - The lead with requested truck types
   * @returns Array of vehicles with similarity information
   */
  const calculateSimilarityForVehicles = (
    vehicles: Vehicle[],
    lead: Lead
  ): VehicleWithSimilarity[] => {
    if (!vehicles || !lead) return [];
    
    // Filter vehicles by status first
    const availableVehicles = vehicles.filter(vehicle => 
      vehicle.status === 'Available' || vehicle.status === 'Pending Available' || vehicle.status === 'Active Deal'
    );
    
    return availableVehicles
      .map(vehicle => calculateVehicleSimilarity(vehicle, lead))
      .filter(vehicle => vehicle.similarityLevel !== 'none')
      .sort((a, b) => {
        // First sort by similarity level (exact first)
        if (a.similarityLevel === 'exact' && b.similarityLevel !== 'exact') return -1;
        if (a.similarityLevel !== 'exact' && b.similarityLevel === 'exact') return 1;
        
        // Then sort by match score (higher first)
        return b.matchScore - a.matchScore;
      });
  };

  /**
   * Function to handle truck type filter changes
   * 
   * @param typeKey - The type key to toggle
   * @param suggestedVehicles - All suggested vehicles
   */
  const handleTruckTypeFilterChange = (
    typeKey: string,
    suggestedVehicles: VehicleWithSimilarity[]
  ) => {
    // Toggle the selected type
    const newSelectedTypes = selectedTruckTypes.includes(typeKey)
      ? selectedTruckTypes.filter(t => t !== typeKey)
      : [...selectedTruckTypes, typeKey];
    
    setSelectedTruckTypes(newSelectedTypes);
    
    // If no types are selected, show all vehicles
    if (newSelectedTypes.length === 0) {
      setFilteredVehicles(suggestedVehicles);
      return;
    }
    
    // Filter vehicles based on selected types
    const filtered = suggestedVehicles.filter(vehicle => 
      vehicle.typeKey && newSelectedTypes.includes(vehicle.typeKey)
    );
    
    setFilteredVehicles(filtered);
  };

  /**
   * Calculates counts of requested and attached trucks by type
   * 
   * @param lead - The lead with truck types
   * @param attachedVehicles - Array of vehicles attached to the lead
   * @returns Array of truck type counts with display information
   */
  const calculateTruckTypeCounts = (
    lead: Lead,
    attachedVehicles: Vehicle[]
  ) => {
    if (!lead.truckRequest?.types || !Array.isArray(lead.truckRequest.types)) {
      return [];
    }
    
    // Initialize counts object
    const typeCounts: {
      [key: string]: {
        index: number;
        type: string[];
        size: string[];
        make: string[];
        model: string[];
        requested: number;
        attached: number;
      }
    } = {};
    
    // Count requested trucks by type
    lead.truckRequest.types.forEach((truckType, index) => {
      const typeKey = `type-${index}`;
      
      if (!typeCounts[typeKey]) {
        typeCounts[typeKey] = {
          index,
          type: truckType.type || ['Any'],
          size: truckType.size || ['Any'],
          make: truckType.make || ['Any'],
          model: truckType.model || ['Any'],
          requested: 0,
          attached: 0
        };
      }
      
      typeCounts[typeKey].requested += truckType.quantity || 1;
    });
    
    // Count attached trucks by type
    attachedVehicles.forEach(vehicle => {
      // Find matching truck type
      for (const [typeKey, count] of Object.entries(typeCounts)) {
        const truckType = lead.truckRequest?.types?.[count.index];
        if (!truckType) continue;
        
        let isMatch = true;
        
        // Check type match - handle type as array
        const typesList = Array.isArray(truckType.type) ? truckType.type : [truckType.type];
        if (typesList.length > 0 && !typesList.includes('Any') && 
            vehicle.type && !typesList.includes(vehicle.type)) {
          isMatch = false;
        }
        
        // Check size match - handle size as array
        const sizesList = Array.isArray(truckType.size) ? truckType.size : [truckType.size];
        if (isMatch && sizesList.length > 0 && !sizesList.includes('Any') && 
            vehicle.size && !sizesList.includes(vehicle.size)) {
          isMatch = false;
        }
        
        // Check make match
        if (isMatch && truckType.make && Array.isArray(truckType.make) && truckType.make.length > 0 && 
            !truckType.make.includes('Any') && !truckType.make.includes(vehicle.make || '')) {
          isMatch = false;
        }
        
        // Check model match
        if (isMatch && truckType.model && Array.isArray(truckType.model) && truckType.model.length > 0 && 
            !truckType.model.includes('Any') && !truckType.model.includes(vehicle.model || '')) {
          isMatch = false;
        }
        
        if (isMatch) {
          typeCounts[typeKey].attached += 1;
          break; // Count each vehicle only once
        }
      }
    });
    
    // Return the counts
    return Object.entries(typeCounts).map(([typeKey, count]) => ({
      typeKey,
      index: count.index,
      type: count.type,
      size: count.size,
      make: count.make,
      model: count.model,
      requested: count.requested,
      attached: count.attached,
      fulfilled: count.attached >= count.requested,
      displayName: getDisplayName(count)
    }));
  };

  return {
    filteredVehicles,
    setFilteredVehicles,
    selectedTruckTypes,
    setSelectedTruckTypes,
    getDisplayName,
    calculateVehicleSimilarity,
    calculateSimilarityForVehicles,
    handleTruckTypeFilterChange,
    calculateTruckTypeCounts
  };
};

// Export types and interfaces for use in other files
export { SimilarityLevel, VehicleWithSimilarity };
